/**
 * The dispatcher's source, as a string.
 *
 * This runs **in the customer's account**, inlined into the CloudFormation
 * template rather than pulled from a bucket Qrvey controls. Two reasons, both
 * about trust: the customer can read exactly what they are agreeing to run, and
 * there is no Qrvey-hosted artifact that could be swapped after they reviewed it.
 *
 * Kept as a string so the tests can *execute* it. Asserting that the deployed
 * code behaves correctly is worth more than asserting that a file mentions the
 * right identifiers, and this is the one component the rest of the suite cannot
 * exercise — it never runs in this account.
 *
 * **It must stay within 4096 characters, which is CloudFormation's limit** for
 * inline Lambda code, and the test measures the string so the failure lands at
 * commit rather than on a customer's deploy. IM-139 spent nearly all the
 * headroom and IM-195 left 36 characters, so v16 (IM-197) bought its space by
 * taking **every comment out of the source**: the reasoning lives here, where
 * it is free, and `docs/run-dispatcher.md` says what the code does for the
 * customer reading it. The comments that used to be inline are kept in *What
 * the inline comments said* below. Do not put them back; the next thing added
 * there has to buy its own space too.
 *
 * ## Why the run id is in `startedBy`
 *
 * Because that is what comes back. Completion reaches Qrvey as an ECS task state
 * change event, and those events carry `startedBy` but **not** the task's tags —
 * so a tag alone would leave the event uncorrelatable with the run that caused
 * it. The tag is kept as well because it is what a human filters on in the ECS
 * console. `startedBy` is capped at 36 characters, which a run id is well
 * inside.
 *
 * ## What each check is for
 *
 * **Caller identity** is proven by IAM before this function is invoked at all —
 * the resource policy admits exactly one role. Nothing the code could do would
 * be stronger, so the code does not attempt it. The external ID is the second
 * factor, checked first here so a caller learns nothing about the grant until
 * they have proved who they are — the same ordering as capability-before-scope
 * in the control plane's own authorization.
 *
 * **Grant expiry** is arithmetic against a timestamp, so it cannot fail to
 * happen — unlike a scheduled revoke, which leaves access open if it does not
 * fire. It is deliberately *not* the only boundary: the runner's own IAM policy
 * carries an `aws:CurrentTime` condition, so AWS refuses the assume-role even if
 * this code were bypassed entirely. This check exists to turn that refusal into a
 * legible reason rather than an opaque `AccessDenied` from inside a container.
 *
 * **The command allow list** matters more than it looks. The value reaches the
 * container's entrypoint, so a passthrough would hand shell to anything able to
 * invoke this function. A map, not a regex — and argv arrays rather than
 * strings, so nothing downstream has to word-split them correctly.
 *
 * **Unexpected fields are rejected, not ignored.** An unknown key means the
 * caller and this dispatcher disagree about the contract, and guessing which of
 * them is right is how a check gets skipped.
 *
 * ## What the dispatcher deliberately never has
 *
 * Customer AWS credentials. The deploy role is assumed inside the runner
 * container, by the task's own role, under the grant condition — see
 * `runner-wrapper.ts`. The dispatcher passes a verb and the Terraform variables
 * and nothing else. So neither the control plane nor the dispatcher ever holds
 * credentials for the customer's account, which is the property that makes a
 * compromise of either survivable.
 *
 * It also no longer knows the deploy role ARN or the lock table: those are
 * fixed per account, so they belong on the task definition rather than in a
 * value this function forwards.
 *
 * ## Why the state location is the exception (IM-139, IM-140)
 *
 * The bucket was fixed there too, on the same reasoning, and it could not stay
 * that way. An **adopted** instance's Terraform state is wherever the installer
 * first put it, in a bucket this stack did not create, and an instance created
 * without a deployment id has a key the control plane minted — neither is
 * derivable from anything the task definition holds. A run that guesses finds
 * no state and builds a *second* instance, with no error to read afterwards.
 *
 * So both travel with the dispatch, and the property the fixed value was
 * protecting is kept where it belongs: a state file holds every secret
 * Terraform touched and the deploy role is an account-wide administrator, so an
 * unchecked bucket name is a way out of the account. The **wrapper** refuses any
 * bucket this account does not own (`head-bucket --expected-bucket-owner`).
 * What this function does is shape validation — a malformed key or bucket is
 * `invalid-state-location`, one reason rather than two, because the caller's fix
 * is the same either way and every character here is charged against the 4096.
 *
 * ## Choosing the installer per run (IM-197, v16)
 *
 * ECS `RunTask` overrides a container's command and environment and **not its
 * image**, so a run on another installer needs a task definition revision that
 * names it. When a run sends an `imageTag` different from the stack's own,
 * `taskFor` reads the stack's revision and registers a copy — only the
 * `runner` container's image changed — in a family of its own,
 * `CHOSEN_IMAGE_FAMILY`, and the run starts from that copy. CloudFormation's
 * revision is never touched, so the stack never drifts.
 *
 * - **A tag, never an image.** The repository is the stack's `RunnerImage`
 *   (`RUNNER_IMAGE` here), and the tag is held to Docker's tag grammar — no
 *   `/`, `@` or `:` — so the control plane can ask for another build of
 *   Qrvey's installer and cannot name any other container. A free-form image
 *   would let a compromised control plane run anything under the deploy role.
 *   No IAM condition reads an image, so this property is held here, in code
 *   the customer reads.
 * - **Only what the template sets is copied** (`KEEP`), never the whole
 *   description: `DescribeTaskDefinition` answers with read-only fields —
 *   `revision`, `status`, `registeredAt` — that `RegisterTaskDefinition`
 *   does not take.
 * - **A fresh copy every time, from the stack's current revision.** Reusing an
 *   earlier copy would run an earlier wrapper after the stack was updated.
 *   Copies accumulate as revisions of the chosen family; they cost nothing,
 *   and the customer may deregister them.
 * - **No tag, or the stack's own, runs exactly as before** — no read, no
 *   register, the stack's revision.
 * - **`RUN_IMAGE_TAG` is the customer's opt-out.** Anything but `allowed` is
 *   treated as `fixed`, failing closed, and a run naming another tag is
 *   `image-tag-fixed`, with the stack's image as the detail. The template also
 *   withholds the describe and register permissions under `fixed`, so the
 *   refusal is IAM's as well as this code's. The stack's own tag is not
 *   "another tag" and runs: it is the image the stack would run anyway.
 *
 * ## What the inline comments said
 *
 * Moved here in v16, keyed by what they annotated:
 *
 * - `CMD` is an allow list, not a passthrough: the value reaches the container
 *   entrypoint. Argv arrays, not strings.
 * - `LOC` is shape only: the wrapper refuses a bucket this account does not
 *   own.
 * - `check` does not authenticate. The resource policy admits one role in
 *   Qrvey's account, so IAM has authenticated the caller before this runs.
 *   The grant check is fail-fast only — IAM enforces the window at
 *   `AssumeRole` — and an empty `GRANT_EXPIRES_AT` defers to it.
 * - `Object.hasOwn`, not `CMD[k]`: an index lookup resolves `constructor` and
 *   `__proto__` off the prototype and would pass the allow list.
 * - A refusal is logged in the customer's account, so it is visible to them
 *   too.
 * - The container overrides carry only what varies per run, and no
 *   credentials. `QRVEY_STATE_KEY` and `QRVEY_STATE_BUCKET` are omitted rather
 *   than blank, because an empty one beats the wrapper's fallback.
 * - `startedBy` carries the run id into the completion event.
 */
/**
 * The task definition family a run on a chosen installer is registered in
 * (IM-197). Not the stack's `qrvey-terraform-runner`, so CloudFormation's own
 * revision is never superseded; the dispatcher's `ecs:RunTask` names both.
 */
export const CHOSEN_IMAGE_FAMILY = 'qrvey-terraform-runner-chosen';
export const DISPATCHER_SOURCE = `'use strict'
const {
  ECSClient, RunTaskCommand, DescribeTaskDefinitionCommand, RegisterTaskDefinitionCommand,
} = require('@aws-sdk/client-ecs')

const CMD = {
  apply: ['apply'], plan: ['plan'], output: ['output'], destroy: ['destroy', '--approve'],
}
const RUN_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/
const LOC = /^[A-Za-z0-9][\\w./-]{0,255}$/
const TAG = /^\\w[\\w.-]{0,127}$/
const FIELDS = ['command', 'runId', 'variables', 'stateKey', 'stateBucket', 'imageTag']
const KEEP = ['taskRoleArn', 'executionRoleArn', 'networkMode', 'requiresCompatibilities', 'cpu',
  'memory', 'runtimePlatform']

const no = (reason, detail) => { throw Object.assign(new Error(reason), { reason, detail }) }

const check = (event, env, now) => {
  const grant = env.GRANT_EXPIRES_AT
  if (grant) {
    const expires = Date.parse(grant.replace(/Z$/, '') + 'Z')
    if (!Number.isFinite(expires)) no('no-grant')
    if (expires <= now) no('grant-expired', { expiredAt: grant })
  }

  const { command, runId, variables: vars, stateKey, stateBucket, imageTag: tag } = event
  if (typeof command !== 'string' || !Object.hasOwn(CMD, command)) no('unknown-command')
  if (typeof runId !== 'string' || !RUN_ID.test(runId)) no('invalid-run-id')
  const extra = Object.keys(event).filter((k) => !FIELDS.includes(k))
  if (extra.length) no('unexpected-fields', { unexpected: extra })
  if (vars !== undefined && (typeof vars !== 'object' || !vars || Array.isArray(vars))) no('invalid-variables')
  const ok = (re, v) => v === undefined || (typeof v === 'string' && re.test(v))
  if (!ok(LOC, stateKey) || !ok(LOC, stateBucket)) no('invalid-state-location')
  if (!ok(TAG, tag)) no('invalid-image-tag')

  const own = env.RUNNER_IMAGE || ''
  const at = own.lastIndexOf(':')
  const image = tag && tag !== own.slice(at + 1) ? own.slice(0, at + 1) + tag : undefined
  if (image && (env.RUN_IMAGE_TAG !== 'allowed' || at < 0)) no('image-tag-fixed', { image: own })
  return { command: CMD[command], runId, variables: vars || {}, stateKey, stateBucket, image }
}

const ecs = new ECSClient({})
const list = (v) => (v || '').split(',').filter(Boolean)
const pair = (name, value) => (value ? [{ name, value }] : [])

const taskFor = async (arn, image) => {
  if (!image) return arn
  const d = (await ecs.send(new DescribeTaskDefinitionCommand({ taskDefinition: arn })))
    .taskDefinition
  const copy = { family: '${CHOSEN_IMAGE_FAMILY}' }
  for (const k of KEEP) copy[k] = d[k]
  copy.containerDefinitions = d.containerDefinitions.map(
    (c) => (c.name === 'runner' ? { ...c, image } : c))
  return (await ecs.send(new RegisterTaskDefinitionCommand(copy))).taskDefinition.taskDefinitionArn
}

const handler = async (event) => {
  const e = process.env
  let req
  try {
    req = check(event || {}, e, Date.now())
  } catch (error) {
    if (!error.reason) throw error
    const refusal = { refused: error.reason, detail: error.detail || null }
    console.warn(JSON.stringify(refusal))
    return { ok: false, ...refusal }
  }

  const r = await ecs.send(new RunTaskCommand({
    cluster: e.CLUSTER_ARN,
    taskDefinition: await taskFor(e.TASK_DEFINITION_ARN, req.image),
    launchType: 'FARGATE',
    networkConfiguration: { awsvpcConfiguration: {
      subnets: list(e.SUBNET_IDS),
      securityGroups: list(e.SECURITY_GROUP_IDS),
      assignPublicIp: e.ASSIGN_PUBLIC_IP || 'DISABLED',
    } },
    overrides: { containerOverrides: [{ name: 'runner', command: req.command, environment: [
      ...pair('QRVEY_RUN_ID', req.runId),
      ...pair('QRVEY_VARIABLES', JSON.stringify(req.variables)),
      ...pair('QRVEY_STATE_KEY', req.stateKey),
      ...pair('QRVEY_STATE_BUCKET', req.stateBucket),
    ] }] },
    startedBy: req.runId,
    tags: [{ key: 'qrvey:run-id', value: req.runId }],
  }))

  const task = r.tasks?.[0]
  if (!task) throw new Error('runner did not start: ' + (r.failures?.[0]?.reason || '?'))
  return { ok: true, runId: req.runId, taskArn: task.taskArn }
}

module.exports = { handler, check, CMD, taskFor }
`;
//# sourceMappingURL=dispatcher-handler.js.map